import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { isAbortError, type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodCompanyAdminSchema } from '../validations';

import { type CompanyAdmin } from './types';
import { zodCompanyAdminEndpointSchema } from './validations';

/**
 * Fetches company data from the API.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Optional request options including abort signal.
 * @returns The company data.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_company_by_id_vadmin_company__company_id__get
 */
export const fetchCompanyAdmin = async (
  companyId: UUID,
  options?: KyRequestOptions,
) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.GET_ADMIN(companyId), {
        validate: {
          pathSegments: zodCompanyAdminEndpointSchema,
          response: zodCompanyAdminSchema,
        },
        ...options,
      })
      .json<CompanyAdmin>();
  } catch (error) {
    if (isAbortError(error)) {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching company',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
